import { useEffect, useState } from 'react';
import { Input, InputNumber, Select, Switch, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, UndoOutlined } from '@ant-design/icons';
import type { ConfigurableRange } from '@/flow-ir/types';
import { FieldHelp } from '@/edu/FieldHelp';
import { getPath, setPath, type FieldSchema } from './schema';

const { Text } = Typography;

/**
 * The schema-driven form engine (spec §6.2). Renders a list of FieldSchema against a target object
 * (the IR node), emitting the whole next object on each edit. Every field shows a PROVENANCE chip
 * (spec §10) — the anti-copy-paste-accident mechanism. PS-2 determines the layers available from the
 * IR alone: 'set' (this flow) vs 'default' (inherited/unset), plus the @configurable range when the
 * node declares one; the full 5-layer resolution (tenant profile / template / parent) arrives with
 * the tenant-profile API (a Track A dependency, noted in the handover).
 */

export interface SchemaFormProps {
  fields: FieldSchema[];
  target: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** The node's @configurable ranges (dotted config paths → {min,max}). */
  configurable?: Record<string, ConfigurableRange>;
  disabled?: boolean;
  /**
   * Capability/node family for field-level help (F-ED-03). When set, each field renders a
   * what/why/example affordance sourced from `GET /v1/edu/field-help?family=`. Absent or unwired →
   * no help icons (graceful).
   */
  helpFamily?: string;
}

function ProvenanceChip({ set }: { set: boolean }) {
  return (
    <span
      style={{
        fontSize: 9.5,
        padding: '0 5px',
        borderRadius: 999,
        border: '1px solid var(--ol-border-subtle)',
        color: set ? 'var(--ol-primary)' : 'var(--ol-text-tertiary)',
        whiteSpace: 'nowrap',
      }}
      title={set ? 'Set on this flow (overridden)' : 'Inherited / default'}
    >
      {set ? 'overridden' : 'default'}
    </span>
  );
}

function JsonField({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const [text, setText] = useState(() => (value === undefined ? '' : JSON.stringify(value, null, 2)));
  const [err, setErr] = useState<string | null>(null);

  // Re-sync when the underlying value changes from outside (undo/redo, node switch).
  useEffect(() => {
    setText(value === undefined ? '' : JSON.stringify(value, null, 2));
    setErr(null);
  }, [value]);

  return (
    <div>
      <Input.TextArea
        className="ol-mono"
        autoSize={{ minRows: 2, maxRows: 8 }}
        value={text}
        disabled={disabled}
        status={err ? 'error' : undefined}
        style={{ fontSize: 11 }}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t.trim() === '') {
            setErr(null);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(t));
            setErr(null);
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      />
      {err && (
        <Text type="danger" style={{ fontSize: 10.5 }}>
          Invalid JSON — not saved until valid.
        </Text>
      )}
    </div>
  );
}

export function SchemaForm({
  fields,
  target,
  onChange,
  configurable,
  disabled,
  helpFamily,
}: SchemaFormProps) {
  const rangeFor = (key: string): ConfigurableRange | undefined => {
    if (!configurable) return undefined;
    const leaf = key.split('.').pop() ?? key;
    return configurable[key] ?? configurable[leaf];
  };

  const set = (key: string, value: unknown) => onChange(setPath(target, key, value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {fields.map((f) => {
        const value = getPath(target, f.key);
        const isSet = value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
        const range = rangeFor(f.key);
        return (
          <div key={f.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', fontWeight: 600 }}>
                {f.label}
                {f.required && <span style={{ color: 'var(--ol-negative)' }}> *</span>}
              </label>
              {f.description && (
                <Tooltip title={f.description}>
                  <QuestionCircleOutlined style={{ fontSize: 11, color: 'var(--ol-text-tertiary)' }} />
                </Tooltip>
              )}
              <FieldHelp family={helpFamily} field={f.key} />
              <div style={{ flex: 1 }} />
              {range && (
                <span
                  style={{ fontSize: 9.5, color: 'var(--ol-attention)' }}
                  title="@configurable range a variant child may override"
                >
                  @cfg [{range.min}–{range.max}]
                </span>
              )}
              <ProvenanceChip set={isSet} />
              {isSet && !f.required && !disabled && (
                <Tooltip title="Reset to inherited / default">
                  <UndoOutlined
                    role="button"
                    aria-label={`Reset ${f.label} to inherited`}
                    tabIndex={0}
                    style={{ fontSize: 11, cursor: 'pointer', color: 'var(--ol-text-tertiary)' }}
                    onClick={() => set(f.key, undefined)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') set(f.key, undefined);
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <FieldControl field={f} value={value} range={range} disabled={disabled} onChange={(v) => set(f.key, v)} />
          </div>
        );
      })}
    </div>
  );
}

function FieldControl({
  field,
  value,
  range,
  disabled,
  onChange,
}: {
  field: FieldSchema;
  value: unknown;
  range?: ConfigurableRange;
  disabled?: boolean;
  onChange: (v: unknown) => void;
}) {
  const min = range?.min ?? field.min;
  const max = range?.max ?? field.max;

  switch (field.type) {
    case 'boolean':
      return <Switch size="small" checked={Boolean(value)} disabled={disabled} onChange={(v) => onChange(v)} />;
    case 'number':
    case 'integer':
      return (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          value={typeof value === 'number' ? value : undefined}
          min={min}
          max={max}
          step={field.step ?? (field.type === 'integer' ? 1 : undefined)}
          precision={field.type === 'integer' ? 0 : undefined}
          disabled={disabled}
          onChange={(v) => onChange(v ?? undefined)}
        />
      );
    case 'enum':
      return (
        <Select
          size="small"
          style={{ width: '100%' }}
          allowClear={!field.required}
          value={typeof value === 'string' ? value : undefined}
          options={(field.enumValues ?? []).map((v) => ({ value: v, label: v }))}
          disabled={disabled}
          onChange={(v) => onChange(v ?? undefined)}
        />
      );
    case 'stringList':
      return (
        <Select
          size="small"
          mode="tags"
          style={{ width: '100%' }}
          placeholder={field.placeholder ?? 'Type and press Enter'}
          value={Array.isArray(value) ? (value as string[]) : []}
          tokenSeparators={[',']}
          disabled={disabled}
          onChange={(v) => onChange((v as string[]).length ? v : undefined)}
        />
      );
    case 'text':
      return (
        <Input.TextArea
          className={field.label.includes('CEL') || field.label.includes('template') ? 'ol-mono' : undefined}
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          disabled={disabled}
          style={{ fontSize: 12 }}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
    case 'json':
      return <JsonField value={value} disabled={disabled} onChange={onChange} />;
    case 'string':
    default:
      return (
        <Input
          size="small"
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
  }
}
